"use client";

import { c as _c } from "react/compiler-runtime";
import { useRouter as useAppRouter, useParams, usePathname, useSearchParams } from "next/navigation";
import { useCallback, useMemo } from "react";
import { usePagesRouterCompat } from "#/shared/hooks/usePagesRouterCompat";
import { parsePlaygroundPathname, PLAYGROUND_PUBLIC_BASE_PATH } from "#/shared/lib/playgroundRoute";
export type PlaygroundNavigateOptions = {
  /** @default false */
  replace?: boolean;
  /** Drop `view` from the next URL. */
  omitView?: boolean;
};
export type PlaygroundRouteContext = {
  basePath: string;
  slug: string[];
  pathname: string;
  navigateTo: (path: string, options?: PlaygroundNavigateOptions) => void;
};

/**
 * Unified playground route state for App Router public paths
 * (`/playground`, `/{lang}/playground`, and legacy pilot bookmarks).
 */
export const usePlaygroundRoute = () => {
  const $ = _c(28);
  const pagesRouter = usePagesRouterCompat();
  const pathname = usePathname();
  const params = useParams();
  const appRouter = useAppRouter();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== pagesRouter) {
    t0 = omitView => {
      if (!pagesRouter) {
        return {};
      }
      const query = {
        ...pagesRouter.query
      };
      delete query.slug;
      if (omitView) {
        delete query.view;
      }
      return query;
    };
    $[0] = pagesRouter;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getPagesQuery = t0;
  let t1;
  if ($[2] !== searchParams) {
    t1 = omitView_0 => {
      const nextParams = new URLSearchParams(searchParams?.toString());
      if (omitView_0) {
        nextParams.delete("view");
      }
      const queryString = nextParams.toString();
      return queryString ? `?${queryString}` : "";
    };
    $[2] = searchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const buildAppQuerySuffix = t1;
  params?.slug;
  let t2;
  bb0: {
    if (pagesRouter) {
      let t3;
      if ($[4] !== pagesRouter.query.slug) {
        t3 = Array.isArray(pagesRouter.query.slug) ? pagesRouter.query.slug : typeof pagesRouter.query.slug === "string" ? [pagesRouter.query.slug] : [];
        $[4] = pagesRouter.query.slug;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      const slug = t3;
      let t4;
      if ($[6] !== getPagesQuery || $[7] !== pagesRouter) {
        t4 = (targetPath, options) => {
          const replace = options?.replace ?? false;
          pagesRouter[replace ? "replace" : "push"]({
            pathname: targetPath,
            query: getPagesQuery(options?.omitView)
          }, undefined, {
            shallow: true
          });
        };
        $[6] = getPagesQuery;
        $[7] = pagesRouter;
        $[8] = t4;
      } else {
        t4 = $[8];
      }
      const navigateTo = t4;
      let t5;
      if ($[9] !== pagesRouter.asPath) {
        t5 = pagesRouter.asPath.split("?")[0] ?? PLAYGROUND_PUBLIC_BASE_PATH;
        $[9] = pagesRouter.asPath;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== navigateTo || $[12] !== slug || $[13] !== t5) {
        t6 = {
          basePath: PLAYGROUND_PUBLIC_BASE_PATH,
          slug,
          pathname: t5,
          navigateTo
        };
        $[11] = navigateTo;
        $[12] = slug;
        $[13] = t5;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      t2 = t6;
      break bb0;
    }
    let t3;
    if ($[15] !== pathname) {
      t3 = pathname ? parsePlaygroundPathname(pathname) : null;
      $[15] = pathname;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    const parsed = t3;
    if (!parsed) {
      t2 = null;
      break bb0;
    }
    const slugParam = params?.slug;
    let t4;
    if ($[17] !== parsed.slug || $[18] !== slugParam) {
      t4 = Array.isArray(slugParam) ? slugParam : typeof slugParam === "string" ? [slugParam] : parsed.slug;
      $[17] = parsed.slug;
      $[18] = slugParam;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    const slug_0 = t4;
    let t5;
    if ($[20] !== appRouter || $[21] !== buildAppQuerySuffix) {
      t5 = (targetPath_0, options_0) => {
        const href = `${targetPath_0}${buildAppQuerySuffix(options_0?.omitView)}`;
        if (options_0?.replace) {
          appRouter.replace(href, {
            scroll: false
          });
          return;
        }
        appRouter.push(href, {
          scroll: false
        });
      };
      $[20] = appRouter;
      $[21] = buildAppQuerySuffix;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    const navigateTo_0 = t5;
    const t6 = pathname ?? parsed.basePath;
    let t7;
    if ($[23] !== navigateTo_0 || $[24] !== parsed.basePath || $[25] !== slug_0 || $[26] !== t6) {
      t7 = {
        basePath: parsed.basePath,
        slug: slug_0,
        pathname: t6,
        navigateTo: navigateTo_0
      };
      $[23] = navigateTo_0;
      $[24] = parsed.basePath;
      $[25] = slug_0;
      $[26] = t6;
      $[27] = t7;
    } else {
      t7 = $[27];
    }
    t2 = t7;
  }
  return t2;
};